import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Card, Empty, Spin, Alert, Tabs, Tooltip, Button, Modal, Space, Switch } from 'antd';
import { InfoCircleOutlined, FullscreenOutlined } from '@ant-design/icons';
//import { EyeOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/useTheme';
import { useEventSelection } from '../../contexts/useEventSelection';
import { useSelectedTimestamp } from '../../contexts/useSelectedTimestamp';
import { useStationObservations } from '../../contexts/useStationObservations';
import { getEventSource, isEventActive as checkEventActive } from '../../utils/eventSource';
import { getConfigOptional } from '../../services/clientConfigService';
import { Timeline } from '../../components/Timeline/Timeline';
import { StationCharts } from '../../components/StationCharts/StationCharts';
import { AllStationsComparison } from '../../components/AllStationsComparison/AllStationsComparison';
import stationObservationsService, { type StationData } from '../../api/stationObservationsService';
import activityService, { type ActivityPeriod, computeActivityRanges } from '../../api/activityService';
import forecastService, { type ForecastDaily, type ForecastHourly } from '../../api/forecastService';

// ActivityAvailability is now imported from activityService

// Per-page storage for Observations live mode "From (MIN)" / "To (MIN)"
const OBSERVATIONS_LIVE_START_KEY = 'observations-live-start-minutes';
const OBSERVATIONS_LIVE_END_KEY = 'observations-live-end-minutes';

function loadObservationsLiveStartMinutes(defaultValue: number, minLimit: number): number {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const stored = localStorage.getItem(OBSERVATIONS_LIVE_START_KEY);
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed >= minLimit && parsed <= 0) return parsed;
    }
  } catch (e) {
    console.warn('Failed to load observations live start minutes:', e);
  }
  return defaultValue;
}

function loadObservationsLiveEndMinutes(defaultValue: number): number {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const stored = localStorage.getItem(OBSERVATIONS_LIVE_END_KEY);
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed >= 1 && parsed <= 60) return parsed;
    }
  } catch (e) {
    console.warn('Failed to load observations live end minutes:', e);
  }
  return defaultValue;
}

interface ForecastAvailability {
  daily?: Date[];
  hourly?: { start: Date; end: Date }[];
  hourlyIssuance?: Date[];
}

const OBSERVATIONS_COMPACT_KEY = 'timeline-compact-observations';

export const ObservationsPage = () => {
  const { theme } = useTheme();
  const { selectedEvent } = useEventSelection();
  const { setSelectedTimestamp } = useSelectedTimestamp();
  const { setExternalStations, setSelectedDateRange: setObservationsContextDateRange } = useStationObservations();
  const [selectedDateRange, setSelectedDateRange] = useState<{ start: Date; end: Date } | null>(null);
  const [stations, setStations] = useState<StationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [radarActivity, setRadarActivity] = useState<ActivityPeriod[]>([]);
  const [stationActivity, setStationActivity] = useState<ActivityPeriod[]>([]);
  const [dailyForecasts, setDailyForecasts] = useState<ForecastDaily[]>([]);
  const [hourlyForecasts, setHourlyForecasts] = useState<ForecastHourly[]>([]);
  const [activeTabKey, setActiveTabKey] = useState<string>('all-stations');
  const [detailModalForTabKey, setDetailModalForTabKey] = useState<string | null>(null);
  const [stackedFullScreen, setStackedFullScreen] = useState(false);
  const [stackedShowLegend, setStackedShowLegend] = useState(true);
  const [windowHeight, setWindowHeight] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 800));
  // Live mode "From (MIN)" limit from system config (observations.max_from_min, default -60)
  const liveStartMinLimit = getConfigOptional<number>('observations.max_from_min') ?? -60;
  // Live mode "From (MIN)" / "To (MIN)" - persisted per-page in localStorage
  const [liveStartMinutes, setLiveStartMinutes] = useState<number>(() =>
    loadObservationsLiveStartMinutes(-60, liveStartMinLimit)
  );
  const [liveEndMinutes, setLiveEndMinutes] = useState<number>(() =>
    loadObservationsLiveEndMinutes(60)
  );
  const [timelineCompact, setTimelineCompact] = useState(() => {
    try { return localStorage.getItem(OBSERVATIONS_COMPACT_KEY) === 'true'; } catch { return false; }
  });
  const handleTimelineCompactChange = useCallback((v: boolean) => {
    setTimelineCompact(v);
    try { localStorage.setItem(OBSERVATIONS_COMPACT_KEY, String(v)); } catch { /* noop */ }
  }, []);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstLoadRef = useRef<boolean>(true);
  // Ref to track the activity refresh interval
  const activityRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Ref for selectedDateRange - used in refreshActivityData to avoid resetting the 5-min interval
  // when LIVE mode updates the range every minute (which would recreate the callback)
  const selectedDateRangeRef = useRef<{ start: Date; end: Date } | null>(null);

  // Fetch observations with time-range filtering and debouncing
  const handleDateRangeChange = useCallback((startDate: Date, endDate: Date) => {
    const isFirstLoad = isFirstLoadRef.current;
    
    // Always update selectedDateRange immediately (for UI feedback)
    const range = { start: startDate, end: endDate };
    setSelectedDateRange(range);
    selectedDateRangeRef.current = range;
    // Tell StationObservationsContext the range so top bar shows latest observation in range
    setObservationsContextDateRange({ start: startDate, end: endDate });
    
    // Only show loading state on first load
    if (isFirstLoad) {
      setLoading(true);
      setError(null);
    } else {
      // Show refreshing state for subsequent loads
      setRefreshing(true);
    }

    // Update selected timestamp for weather parameter components in header
    // Use endDate as fallback (we show latest observation in range, so end is appropriate placeholder)
    setSelectedTimestamp(endDate);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the API call
    debounceTimerRef.current = setTimeout(async () => {
      if (!selectedEvent) return;

      try {
        // Determine source based on event type
        const source = getEventSource(selectedEvent);

        const response = await stationObservationsService.getStationObservations(
          selectedEvent.season_year,
          selectedEvent.circuit_id,
          selectedEvent.start_date,
          source, // Use the event source directly
          startDate.toISOString(),
          endDate.toISOString()
        );

        // Silent update - just update the data without showing loading state
        setStations(response.stations);
        setError(null); // Clear any previous errors
        
        // After first successful load, mark as no longer first load
        if (isFirstLoad) {
          isFirstLoadRef.current = false;
          setLoading(false);
        } else {
          setRefreshing(false);
        }
      } catch (err) {
        console.error('Error fetching observation data:', err);
        // Only show error on first load
        if (isFirstLoad) {
          setError(err instanceof Error ? err.message : 'Failed to load observation data');
          setStations([]);
          setLoading(false);
        } else {
          // Silent error - just log it, don't disrupt the UI
          console.warn('Silent refresh failed:', err);
          setRefreshing(false);
        }
      }
    }, 500); // 500ms debounce
  }, [selectedEvent, setSelectedTimestamp, setObservationsContextDateRange]);

  // Keep ref in sync with state
  useEffect(() => {
    selectedDateRangeRef.current = selectedDateRange;
  }, [selectedDateRange]);

  // Clear data when event changes
  useEffect(() => {
    if (!selectedEvent) {
      setStations([]);
      setSelectedDateRange(null);
      selectedDateRangeRef.current = null;
      setError(null);
      setDailyForecasts([]);
      setHourlyForecasts([]);
      setSelectedTimestamp(null);
      setActiveTabKey('all-stations');
      setDetailModalForTabKey(null);
      setStackedFullScreen(false);
      setStackedShowLegend(true);
      setRefreshing(false);
      isFirstLoadRef.current = true; // Reset first load flag when event changes
      setExternalStations(null);
      setObservationsContextDateRange(null);
    }
  }, [selectedEvent, setSelectedTimestamp, setExternalStations, setObservationsContextDateRange]);

  // Share stations data with StationObservationsContext to avoid duplicate API calls
  // The context will use this data instead of fetching its own 10-minute window
  useEffect(() => {
    setExternalStations(stations.length > 0 ? stations : null);
  }, [stations, setExternalStations]);

  // Cleanup: Clear external stations and date range when component unmounts
  useEffect(() => {
    return () => {
      setExternalStations(null);
      setObservationsContextDateRange(null);
    };
  }, [setExternalStations, setObservationsContextDateRange]);

  // Refresh handler for manual refresh button - refreshes both chart data and timeline activity
  const handleRefresh = useCallback(() => {
    if (!selectedDateRange || !selectedEvent) return;
    
    setRefreshing(true);
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Fetch chart data, radar activity, and station activity in parallel
    (async () => {
      try {
        const source = getEventSource(selectedEvent);
        const radarSource = source === 'legacy' ? 'legacy' : 'l2';
        const [observationsResponse, radarResponse, stationResponse] = await Promise.allSettled([
          stationObservationsService.getStationObservations(
            selectedEvent.season_year,
            selectedEvent.circuit_id,
            selectedEvent.start_date,
            source,
            selectedDateRange.start.toISOString(),
            selectedDateRange.end.toISOString()
          ),
          activityService.getRadarActivity({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source: radarSource,
          }),
          activityService.getStationActivity({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
        ]);

        if (observationsResponse.status === 'fulfilled') {
          setStations(observationsResponse.value.stations);
          setError(null);
        } else if (observationsResponse.status === 'rejected') {
          throw observationsResponse.reason;
        }

        if (radarResponse.status === 'fulfilled' && radarResponse.value.activities.length > 0) {
          setRadarActivity(radarResponse.value.activities);
        } else if (radarResponse.status === 'fulfilled') {
          setRadarActivity([]);
        }

        if (stationResponse.status === 'fulfilled' && stationResponse.value.activities.length > 0) {
          setStationActivity(stationResponse.value.activities);
        } else if (stationResponse.status === 'fulfilled') {
          setStationActivity([]);
        }
      } catch (err) {
        console.error('Error refreshing observation data:', err);
        setError(err instanceof Error ? err.message : 'Failed to refresh observation data');
      } finally {
        setRefreshing(false);
      }
    })();
  }, [selectedDateRange, selectedEvent]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Persist live mode time range settings to localStorage (per-page)
  useEffect(() => {
    try {
      localStorage.setItem(OBSERVATIONS_LIVE_START_KEY, liveStartMinutes.toString());
    } catch (error) {
      console.error('Error saving observations liveStartMinutes to localStorage:', error);
    }
  }, [liveStartMinutes]);

  useEffect(() => {
    try {
      localStorage.setItem(OBSERVATIONS_LIVE_END_KEY, liveEndMinutes.toString());
    } catch (error) {
      console.error('Error saving observations liveEndMinutes to localStorage:', error);
    }
  }, [liveEndMinutes]);

  // Check if the event is currently active (for live updates)
  // Uses circuit timezone for consistent behavior across the app
  const isEventActive = checkEventActive(selectedEvent);

  // Function to refresh activity data (used on interval for live events)
  const refreshActivityData = useCallback(async () => {
    if (!selectedEvent || !isEventActive) return;

    try {
      const source = getEventSource(selectedEvent);
      const radarSource = source === 'legacy' ? 'legacy' : 'l2';

      // Build promises: radar + station activity, and optionally station observations
      const radarPromise = activityService.getRadarActivity({
        season_year: selectedEvent.season_year,
        circuit_id: selectedEvent.circuit_id,
        start_date: selectedEvent.start_date,
        source: radarSource,
      });
      const stationPromise = activityService.getStationActivity({
        season_year: selectedEvent.season_year,
        circuit_id: selectedEvent.circuit_id,
        start_date: selectedEvent.start_date,
        source,
      });
      const currentRange = selectedDateRangeRef.current;
      const observationsPromise = currentRange
        ? stationObservationsService.getStationObservations(
            selectedEvent.season_year,
            selectedEvent.circuit_id,
            selectedEvent.start_date,
            source,
            currentRange.start.toISOString(),
            currentRange.end.toISOString()
          )
        : null;

      const [radarResponse, stationResponse, observationsResponse] = await Promise.allSettled([
        radarPromise,
        stationPromise,
        ...(observationsPromise ? [observationsPromise] : []),
      ]);

      if (radarResponse.status === 'fulfilled' && radarResponse.value.activities.length > 0) {
        setRadarActivity(radarResponse.value.activities);
        console.log('[Observations] Refreshed radar activity');
      }

      if (stationResponse.status === 'fulfilled' && stationResponse.value.activities.length > 0) {
        setStationActivity(stationResponse.value.activities);
        console.log('[Observations] Refreshed station activity');
      }

      if (observationsPromise && observationsResponse?.status === 'fulfilled' && observationsResponse.value.stations) {
        setStations(observationsResponse.value.stations);
        console.log('[Observations] Refreshed station observations');
      }
    } catch (err) {
      console.error('[Observations] Error refreshing activity data:', err);
    }
  }, [selectedEvent, isEventActive]);

  // Set up 5-minute interval to refresh activity data for live events
  // NOTE: We intentionally do NOT refresh on visibility/focus changes anymore.
  // This was causing bad UX where the page would refresh unnecessarily when
  // switching browser tabs. Instead, we use a periodic interval for live events.
  useEffect(() => {
    // Clear any existing interval
    if (activityRefreshIntervalRef.current) {
      clearInterval(activityRefreshIntervalRef.current);
      activityRefreshIntervalRef.current = null;
    }

    // Only set up interval for active events
    if (!isEventActive) {
      return;
    }

    // Refresh activity data every 5 minutes for live events
    const ACTIVITY_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
    
    console.log('[Observations] Setting up 5-minute activity refresh interval for live event');
    activityRefreshIntervalRef.current = setInterval(() => {
      console.log('[Observations] 5-minute interval: refreshing activity data');
      refreshActivityData();
    }, ACTIVITY_REFRESH_INTERVAL);

    return () => {
      if (activityRefreshIntervalRef.current) {
        clearInterval(activityRefreshIntervalRef.current);
        activityRefreshIntervalRef.current = null;
      }
    };
  }, [isEventActive, refreshActivityData]);

  // Fetch activity data when event changes
  useEffect(() => {
    if (!selectedEvent) return;

    const fetchAvailability = async () => {
      try {
        // Determine source based on legacy flag (observations page doesn't have legacy API toggle)
        const source = getEventSource(selectedEvent);
        const radarSource = source === 'legacy' ? 'legacy' : 'l2';

        // Fetch all availability data in parallel
        const [radarResponse, stationResponse, dailyResponse, hourlyResponse] = await Promise.allSettled([
          activityService.getRadarActivity({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source: radarSource,
          }),
          activityService.getStationActivity({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
          forecastService.getForecastDaily({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
          forecastService.getForecastHourly({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
        ]);

        // Set radar activity data
        if (radarResponse.status === 'fulfilled' && radarResponse.value.activities.length > 0) {
          setRadarActivity(radarResponse.value.activities);
        } else {
          setRadarActivity([]);
        }

        // Set station activity data
        if (stationResponse.status === 'fulfilled' && stationResponse.value.activities.length > 0) {
          setStationActivity(stationResponse.value.activities);
        } else {
          setStationActivity([]);
        }

        // Set daily forecasts
        if (dailyResponse.status === 'fulfilled' && dailyResponse.value.forecasts.length > 0) {
          setDailyForecasts(dailyResponse.value.forecasts);
        } else {
          setDailyForecasts([]);
        }

        // Set hourly forecasts
        if (hourlyResponse.status === 'fulfilled' && hourlyResponse.value.forecasts.length > 0) {
          setHourlyForecasts(hourlyResponse.value.forecasts);
        } else {
          setHourlyForecasts([]);
        }
      } catch (err) {
        console.error('Error fetching availability data:', err);
        // Don't set as a critical error, just log it
      }
    };

    fetchAvailability();
  }, [selectedEvent]);

  // Calculate radar activity availability ranges
  const radarActivityAvailability = radarActivity.length > 0
    ? computeActivityRanges(radarActivity)
    : undefined;

  // Calculate station activity availability ranges
  const stationActivityAvailability = stationActivity.length > 0
    ? computeActivityRanges(stationActivity)
    : undefined;

  // Track window resize when fullscreen modal is open (for dynamic chart height)
  useEffect(() => {
    if (!stackedFullScreen) return;
    const onResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [stackedFullScreen]);

  // Compute chart height for fullscreen modal so all stations fit without scrolling
  // Body is calc(100vh - 110px), minus padding (32) and small safety buffer
  const { fullscreenChartHeight, fullscreenNeedsScroll } = useMemo(() => {
    if (stations.length === 0) return { fullscreenChartHeight: 150, fullscreenNeedsScroll: false };
    const bodyHeight = windowHeight - 110 - 32 - 35; // modal chrome, padding, safety buffer
    const gap = 8;
    const rowOverhead = 70; // station title, chart titles, padding
    const legendOverhead = stackedShowLegend ? 50 : 0;
    const availablePerStation = (bodyHeight - (stations.length - 1) * gap) / stations.length;
    const computed = Math.floor(availablePerStation - rowOverhead - legendOverhead);
    const chartHeight = Math.max(80, Math.min(250, computed));
    const needsScroll = computed < 80; // content won't fit, allow scroll
    return { fullscreenChartHeight: chartHeight, fullscreenNeedsScroll: needsScroll };
  }, [stations.length, stackedShowLegend, windowHeight]);

  // Calculate forecast issuance times and coverage ranges
  const forecastAvailability = useMemo<ForecastAvailability>(() => {
    const availability: ForecastAvailability = {};

    // Extract daily forecast issuance times
    if (dailyForecasts.length > 0) {
      availability.daily = dailyForecasts.map(f => new Date(f.value_date));
    }

    // Extract hourly forecast issuance times
    if (hourlyForecasts.length > 0) {
      availability.hourlyIssuance = hourlyForecasts.map(f => new Date(f.value_date));
    }

    // Extract hourly forecast coverage ranges
    if (hourlyForecasts.length > 0) {
      const ranges: { start: Date; end: Date }[] = [];

      hourlyForecasts.forEach(forecast => {
        if (forecast.hourly_forecasts && forecast.hourly_forecasts.length > 0) {
          const forecastTimes: number[] = [];

          forecast.hourly_forecasts.forEach(hf => {
            const datetimeValue = typeof hf.forecast_datetime === 'object' && hf.forecast_datetime !== null && 'value' in hf.forecast_datetime
              ? (hf.forecast_datetime as { value: string }).value
              : hf.forecast_datetime;

            if (datetimeValue) {
              forecastTimes.push(new Date(datetimeValue).getTime());
            }
          });

          if (forecastTimes.length > 0) {
            const minTime = Math.min(...forecastTimes);
            const maxTime = Math.max(...forecastTimes);
            ranges.push({
              start: new Date(minTime),
              end: new Date(maxTime)
            });
          }
        }
      });

      availability.hourly = ranges;
    }

    return availability;
  }, [dailyForecasts, hourlyForecasts]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Sticky Header */}
      {/* <div style={{
        position: 'sticky',
        top: 0,
        backgroundColor: theme === 'dark' ? '#141414' : '#f0f2f5',
        zIndex: 10,
        paddingBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}><EyeOutlined /> Observations</h1>
          <span style={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)' }}>
            Historical and real-time weather observations from track stations.
          </span>
        </div>
      </div> */}

      {/* Content Area */}
      {!selectedEvent ? (
        <Card>
          <Empty description="Please select an event to view observations" />
        </Card>
      ) : (
        <>
          {/* Timeline */}
          <div style={{
            flexShrink: 0,
          }}>
            <Timeline
              onDateRangeChange={handleDateRangeChange}
              forecastAvailability={forecastAvailability}
              radarActivityAvailability={radarActivityAvailability}
              stationActivityAvailability={stationActivityAvailability}
              liveStartMinutes={liveStartMinutes}
              liveEndMinutes={liveEndMinutes}
              onLiveStartMinutesChange={setLiveStartMinutes}
              onLiveEndMinutesChange={setLiveEndMinutes}
              mapTileProvider="radar"
              liveEndMinutesForRadar={0}
              liveStartMinLimit={liveStartMinLimit}
              refreshing={refreshing}
              onRefresh={selectedDateRange ? handleRefresh : undefined}
              clampSelectionToNow={isEventActive}
              compact={timelineCompact}
              onCompactChange={handleTimelineCompactChange}
            />
          </div>

          {/* Scrollable Charts Area */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
            {!selectedDateRange ? (
              <Card>
                <Empty description="Select a time range on the timeline above to load observation data" />
              </Card>
            ) : loading && stations.length === 0 ? (
              <Card>
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 16, color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)' }}>
                    Loading observation data for selected time range...
                  </div>
                </div>
              </Card>
            ) : error && stations.length === 0 ? (
              <Card>
                <Alert
                  title="Error Loading Data"
                  description={error}
                  type="error"
                  showIcon
                />
              </Card>
            ) : stations.length === 0 ? (
              <Card>
                <Alert
                  title="No Data Available"
                  description={
                    <div>
                      <p>No observation data is available for this event and time range.</p>
                      <p style={{ marginTop: 8, marginBottom: 0 }}>
                        Data may not have been collected for this event yet, or it hasn't been uploaded to the system.
                        Try selecting a different time range or event.
                      </p>
                    </div>
                  }
                  type="info"
                  showIcon
                />
              </Card>
            ) : (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <style>{`
                  .observations-tabs {
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                  }
                  .observations-tabs > .ant-tabs-nav {
                    margin: 0 !important;
                  }
                  .observations-tabs > .ant-tabs-content-holder {
                    flex: 1;
                    margin-top: 0 !important;
                    min-height: 0;
                  }
                  .observations-tabs > .ant-tabs-content-holder > .ant-tabs-content {
                    height: 100%;
                  }
                  .observations-tabs > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane {
                    height: 100%;
                    overflow-y: auto;
                  }
                `}</style>
                <Tabs
                  activeKey={activeTabKey}
                  onChange={(key) => {
                    setActiveTabKey(key);
                    setDetailModalForTabKey(null);
                  }}
                  type="card"
                  className="observations-tabs"
                  items={[
                    {
                      key: 'all-stations',
                      label: 'All Stations',
                      children: (
                        <AllStationsComparison
                          stations={stations}
                          circuitTimeZone={selectedEvent.circuit?.time_zone}
                          sessions={selectedEvent?.sessions || []}
                          dateRange={selectedDateRange ?? undefined}
                        />
                      ),
                    },
                    {
                      key: 'stacked',
                      label: 'Stacked',
                      children: (
                        <Card
                          styles={{
                            body: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: '16px' },
                          }}
                          title={
                            <span style={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)' }}>
                              Stacked View
                            </span>
                          }
                          extra={
                            <Space align="center">
                              <span style={{ fontSize: 12, color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)' }}>
                                Show legend
                              </span>
                              <Switch
                                size="small"
                                checked={stackedShowLegend}
                                onChange={setStackedShowLegend}
                              />
                              <Button
                                type="text"
                                icon={<FullscreenOutlined />}
                                onClick={() => setStackedFullScreen(true)}
                                title="Full screen charts"
                                style={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)' }}
                              />
                            </Space>
                          }
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                            {stations.map((station, index) => (
                              <div key={`stacked-${index}`} style={{ display: 'flex', flexDirection: 'row', gap: 16, alignItems: 'flex-start' }}>
                                <div
                                  style={{
                                    flexShrink: 0,
                                    width: 100,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                  }}
                                >
                                  Ground Station {station.station_reference}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <StationCharts
                                  station={station}
                                  seasonYear={selectedEvent.season_year}
                                  circuitId={selectedEvent.circuit_id}
                                  geojsonId={selectedEvent.circuit_geojson_id}
                                  selectedStartDate={selectedDateRange.start}
                                  selectedEndDate={selectedDateRange.end}
                                  circuitTimeZone={selectedEvent.circuit?.time_zone}
                                  stationLat={selectedEvent.stations?.find(s => s.reference === station.station_reference)?.lat}
                                  stationLon={selectedEvent.stations?.find(s => s.reference === station.station_reference)?.lon}
                                  sessions={selectedEvent?.sessions || []}
                                  detailModalOpen={false}
                                  onDetailModalClose={() => {}}
                                  hideLegend={!stackedShowLegend}
                                />
                                </div>
                              </div>
                            ))}
                          </div>
                        </Card>
                      ),
                    },
                    ...stations.map((station, index) => {
                      const tabKey = `station-${index}`;
                      return {
                        key: tabKey,
                        label: (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>Ground Station {station.station_reference}</span>
                            <Tooltip title="View detailed data">
                              <InfoCircleOutlined
                                style={{ fontSize: 12, opacity: 0.7, cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDetailModalForTabKey(tabKey);
                                }}
                              />
                            </Tooltip>
                          </span>
                        ),
                        children: (
                          <StationCharts
                            station={station}
                            seasonYear={selectedEvent.season_year}
                            circuitId={selectedEvent.circuit_id}
                            geojsonId={selectedEvent.circuit_geojson_id}
                            selectedStartDate={selectedDateRange.start}
                            selectedEndDate={selectedDateRange.end}
                            circuitTimeZone={selectedEvent.circuit?.time_zone}
                            stationLat={selectedEvent.stations?.find(s => s.reference === station.station_reference)?.lat}
                            stationLon={selectedEvent.stations?.find(s => s.reference === station.station_reference)?.lon}
                            sessions={selectedEvent?.sessions || []}
                            detailModalOpen={detailModalForTabKey === tabKey}
                            onDetailModalClose={() => setDetailModalForTabKey(null)}
                          />
                        ),
                      };
                    }),
                  ]}
                />
                {/* Fullscreen modal for Stacked tab */}
                <Modal
                  open={stackedFullScreen}
                  onCancel={() => setStackedFullScreen(false)}
                  footer={null}
                  width="100%"
                  styles={{
                    body: {
                      height: 'calc(100vh - 110px)',
                      maxHeight: 'calc(100vh - 110px)',
                      padding: 16,
                      overflow: fullscreenNeedsScroll ? 'auto' : 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: theme === 'dark' ? '#1f1f1f' : '#fff',
                    },
                    wrapper: { maxWidth: '100vw' },
                  }}
                  style={{ top: 24, paddingBottom: 0 }}
                  title={
                    <Space align="center">
                      <span>Stacked View - Full Screen</span>
                      <Space align="center" style={{ marginLeft: 16 }}>
                        <span style={{ fontSize: 12, fontWeight: 'normal' }}>Show legend</span>
                        <Switch
                          size="small"
                          checked={stackedShowLegend}
                          onChange={setStackedShowLegend}
                        />
                      </Space>
                    </Space>
                  }
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflow: 'hidden', paddingBottom: 8 }}>
                    {stations.map((station, index) => (
                      <div key={`stacked-modal-${index}`} style={{ display: 'flex', flexDirection: 'row', gap: 16, alignItems: 'flex-start', flexShrink: 0 }}>
                        <div
                          style={{
                            flexShrink: 0,
                            width: 100,
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                          }}
                        >
                          Ground Station {station.station_reference}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <StationCharts
                          station={station}
                          seasonYear={selectedEvent.season_year}
                          circuitId={selectedEvent.circuit_id}
                          geojsonId={selectedEvent.circuit_geojson_id}
                          selectedStartDate={selectedDateRange!.start}
                          selectedEndDate={selectedDateRange!.end}
                          circuitTimeZone={selectedEvent.circuit?.time_zone}
                          stationLat={selectedEvent.stations?.find(s => s.reference === station.station_reference)?.lat}
                          stationLon={selectedEvent.stations?.find(s => s.reference === station.station_reference)?.lon}
                          sessions={selectedEvent?.sessions || []}
                          detailModalOpen={false}
                          onDetailModalClose={() => {}}
                          hideLegend={!stackedShowLegend}
                          chartHeight={fullscreenChartHeight}
                        />
                        </div>
                      </div>
                    ))}
                  </div>
                </Modal>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

